import React, { useState, useEffect } from "react";
import { useProfileField } from "@/hooks/useHealthProfile";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { IconCaretDownFilled } from "@tabler/icons-react";
import FormHeader from "@/components/ui/organisms/MHeader";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { MChip } from "@/components/ui/atoms/MChip";
import { useProfileUpdate } from "@/hooks/useMembers";

import {
  BloodType,
  CatBloodType,
  DogBloodType,
  MedicalDetailType,
} from "@/types/health-profile";
import { useTranslation } from "react-i18next";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MInputField from "@/components/ui/atoms/MInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useNavigate } from "react-router-dom";
import { trackUserDataLoaded } from "@/utils/gtm";
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';
import { useNavigationSource } from '@/hooks/useNavigationSource';

const BloodGroup: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { updateMemberInLocalStorage } = useProfileUpdate();
  
  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();

  // Use correct property names for member type and pet type
  const memberType = memberInfo.memberType || "h";
  const petType = memberInfo.other_attrs?.petType;

  let bloodGroupEnum: any;
  if (memberType === "p") {
    if (petType === "cat") {
      bloodGroupEnum = CatBloodType;
    } else if (petType === "dog") {
      bloodGroupEnum = DogBloodType;
    } else {
      bloodGroupEnum = BloodType; // fallback for other pets
    }
  } else {
    bloodGroupEnum = BloodType;
  }

  const bloodGroupOptions = Object.entries(bloodGroupEnum).map(
    ([_, value]) => ({
      label: t(`healthProfile.bloodType.${value}`),
      value: String(value),
    })
  );

  const {
    fieldValue: bloodGroup,
    setFieldValue: setBloodGroup,
    saveField: saveBloodGroup,
    isChanged,
    loading,
  } = useProfileField<string>(memberId, MedicalDetailType.BLOOD_TYPE, "");

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.BLOOD_GROUP,
    });
  }, []);

  const handleDone = async () => {
    try {
      await saveBloodGroup(bloodGroup);
      setDrawerOpen(false);
      updateMemberInLocalStorage(memberId, memberInfo.onBehalf);
      
      // Navigate back to source if exists, otherwise go to health profile
      const didNavigate = navigateBackIfExists();
      if (!didNavigate) {
        navigate("/profile/health-profile");
      }
    } catch (error) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    }
  };

  const getIsButtonDisabled = () => {
    console.log("isChanged:", isChanged());
    return loading || !isChanged();
  };

  const getLabel = () => {
    return memberInfo.onBehalf ? t("bloodGroup.other.title") : t("bloodGroup.title");
  };

  return (
    <div className="profile-page-root">
      <FormHeader module="blood" memberInfo={memberInfo} hideLearnMore />
      <MSpacer />
      <form
        className="flex flex-col gap-6 flex-1"
        onSubmit={(e) => {
          e.preventDefault();
          handleDone();
        }}
      >
        <MInputField
          label={getLabel()}
          value={bloodGroup}
          placeholder={t("common.enterOrChoose")}
          onClick={() => setDrawerOpen(true)}
          disabled={loading}
          icon={<IconCaretDownFilled size={20} />}
          showIcon={true}
        />
      </form>
      <div className="confirm-footer">
        <MButton
          size="lg"
          variant="filled"
          text={t("common.done")}
          className="w-full"
          disabled={getIsButtonDisabled()}
          onClick={handleDone}
        />
      </div>
      <FooterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t("bloodGroup.select-title")}
        showClose
      >
        <div className="blood-group-drawer">
          <div className="blood-group-chips">
            {bloodGroupOptions.map((opt) => (
              <MChip
                key={opt.value}
                text={opt.label}
                selected={bloodGroup === opt.value}
                variant="drawerChip"
                onClick={() => {
                  setBloodGroup(opt.value);
                  setDrawerOpen(false);
                }}
                rightIcon={false}
              />
            ))}
          </div>
        </div>
      </FooterDrawer>
    </div>
  );
};

export default BloodGroup;
